/**
 * Faire défiler la page pendant qu'on glisse un bloc.
 *
 * *Signalé par Chris le 23/09/2026 : « lorsqu'on doit déplacer un chapitre il faut pouvoir
 * descendre aussi loin que l'on veut ; quand on arrive en bas de la page cela ne scrolle pas
 * pour descendre et lâcher le chapitre plus bas. »*
 *
 * **Le glisser-déposer natif ne fait pas défiler.** Tant que le bouton est enfoncé, la
 * molette et les barres de défilement ne répondent plus, et le navigateur ne défile pas de
 * lui-même de façon fiable. Sur un déboursé de 130 lignes qui descend à plus de 3 800 px,
 * tout ce qui n'est pas déjà à l'écran est donc **hors d'atteinte** — le geste n'existe que
 * pour les blocs voisins, c'est-à-dire précisément ceux qu'on n'a pas besoin de déplacer.
 *
 * ## Pourquoi une boucle d'animation, et pas l'événement de survol
 *
 * Le premier réflexe est de défiler d'un cran à chaque `dragover`. Il ne tient pas : cet
 * événement suit la SOURIS, et la souris ne bouge justement plus quand on est arrivé au
 * bord en attendant que la page descende. La spécification le fait bien se répéter environ
 * toutes les 350 ms sans mouvement, mais un défilement à trois images par seconde n'est pas
 * un défilement. La boucle, elle, tourne à la fréquence de l'écran tant que le curseur reste
 * dans la bande, que la souris bouge ou non.
 */

/** La hauteur de la bande sensible, en haut et en bas de la fenêtre. */
export const BANDE = 110;

/** Le défilement le plus rapide, en pixels par image — soit environ 1 200 px/s à 60 Hz. */
export const VITESSE_MAX = 20;

/**
 * De combien de pixels défiler, d'après la position verticale du curseur.
 *
 * Négatif vers le haut, positif vers le bas, zéro hors des bandes. La vitesse est
 * **proportionnelle à l'enfoncement dans la bande** plutôt que constante : au bord de la
 * bande on avance d'une ligne à la fois, ce qu'il faut pour se poser entre deux postes
 * précis, et contre le bord de l'écran on traverse le déboursé. Une vitesse unique
 * obligerait à choisir entre « trop lent pour descendre de 3 800 px » et « impossible à
 * arrêter au bon endroit ».
 */
export function vitesseDeDefilement(
  y: number,
  hauteurFenetre: number,
  bande: number = BANDE,
  vitesseMax: number = VITESSE_MAX,
): number {
  // Une fenêtre plus courte que deux bandes n'aurait aucune zone neutre au milieu : les
  // deux se chevaucheraient et le moindre survol déclencherait un défilement. On rétrécit
  // plutôt que de renoncer — le geste doit rester possible sur un écran bas.
  const utile = Math.min(bande, Math.max(0, hauteurFenetre / 2 - 1));
  if (utile <= 0) return 0;

  if (y < utile) {
    const enfoncement = (utile - y) / utile;
    return -Math.ceil(Math.min(1, enfoncement) * vitesseMax);
  }
  if (y > hauteurFenetre - utile) {
    const enfoncement = (y - (hauteurFenetre - utile)) / utile;
    return Math.ceil(Math.min(1, enfoncement) * vitesseMax);
  }
  return 0;
}
